import { useState } from 'react';
import type { ComponentData } from '../types';

interface ComponentCardProps {
    component: ComponentData;
}

export function ComponentCard({ component }: ComponentCardProps) {
    const [isCopied, setIsCopied] = useState(false);

    const [isCopying, setIsCopying] = useState(false);

    const [componentData, setComponentData] = useState<any>(component.data);
    const [isLoading, setIsLoading] = useState(false);

    const prefetchData = async () => {
        if (componentData || isLoading) return;
        setIsLoading(true);
        try {
            const response = await fetch(`/api/components/${component.id}`);
            if (!response.ok) throw new Error("Failed to fetch component data");
            const fullComponent = await response.json();
            setComponentData(fullComponent.data);
        } catch (error) {
            console.error("Prefetch failed:", error);
        } finally {
            setIsLoading(false);
        }
    };

    const copyJsonToClipboard = async () => {
        setIsCopying(true);
        try {
            let dataToCopy = componentData;

            // If data is not yet loaded (user clicked fast), fetch it now
            if (!dataToCopy) {
                const response = await fetch(`/api/components/${component.id}`);
                if (!response.ok) throw new Error("Failed to fetch component data");
                const fullComponent = await response.json();
                dataToCopy = fullComponent.data;
                setComponentData(dataToCopy);
            }

            const jsonString = JSON.stringify(dataToCopy, null, 2);

            // Strategy: Use a hidden textarea + execCommand('copy') + intercepting the copy event.
            // This is currently the ONLY way to write 'application/json' to the clipboard on many browsers,
            // as navigator.clipboard.write() with ClipboardItem often throws "Type not supported" for non-standard MIME types.

            const textarea = document.createElement("textarea");
            textarea.value = jsonString;
            textarea.style.position = "fixed";
            textarea.style.opacity = "0";
            document.body.appendChild(textarea);
            textarea.focus();
            textarea.select();

            let handled = false;
            const copyHandler = (e: ClipboardEvent) => {
                e.preventDefault();
                if (e.clipboardData) {
                    e.clipboardData.setData("application/json", jsonString);
                    // e.clipboardData.setData("text/plain", jsonString);
                    handled = true;
                }
            };

            // Attach to document to ensure we catch the event
            document.addEventListener("copy", copyHandler);

            try {
                const success = document.execCommand("copy");
                if (!success && !handled) {
                    throw new Error("execCommand returned false");
                }
            } catch (err) {
                console.warn("execCommand failed, falling back to writeText:", err);
                // Fallback to simple text copy (loses application/json but better than nothing)
                if (navigator.clipboard && navigator.clipboard.writeText) {
                    await navigator.clipboard.writeText(jsonString);
                }
            } finally {
                document.removeEventListener("copy", copyHandler);
                document.body.removeChild(textarea);
                setIsCopying(false);
            }

            setIsCopied(true);
            setTimeout(() => {
                setIsCopied(false);
            }, 2000);
        } catch (error) {
            console.error("Copy failed:", error);
            setIsCopying(false);
        }
    };

    return (
        <div className="component-card">
            <div className="component-preview">
                <img
                    src={`/cms-data/components/${component.id}/thumbnail.webp`}
                    alt={component.name}
                    className="component-thumbnail"
                    loading="lazy"
                />
            </div>
            <div className="component-footer">
                <h3 className="component-name">{component.name}</h3>
                <div className="component-actions">
                    <button
                        onClick={copyJsonToClipboard}
                        onMouseEnter={prefetchData}
                        onFocus={prefetchData}
                        className="copy-button"
                    >
                        {isCopied ? 'Copied' : (isCopying ? 'Copying...' : 'Copy')}
                    </button>
                    <button
                        onClick={() => window.open(`/cms-data/components/${component.id}/cover.png`, '_blank')}
                        className="view-button"
                        title="View Cover"
                    >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                            <circle cx="12" cy="12" r="3"></circle>
                        </svg>
                    </button>
                </div>
            </div>
        </div>
    );
}
